import { useState, useEffect, useCallback, useRef } from 'react';
import { CctpService } from '@trace/connector';
import type { OdooClient } from '@trace/connector';
import type { Project, LotValues } from '../types.js';

export function useCctpProject(client: OdooClient | null, projectId: number | null, debounceMs = 600) {
  const [project, setProject] = useState<Project | null>(null);
  const [lots, setLots] = useState<LotValues>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [dirty, setDirty] = useState<Record<string, { lotCode: string; fieldName: string; value: unknown }>>({});
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!client || !projectId) return;
    let cancelled = false;
    setLoading(true);
    const svc = new CctpService(client);
    svc.loadProject(projectId)
      .then(d => {
        if (cancelled) return;
        setProject(d.project as Project);
        setLots(d.lots as LotValues);
      })
      .catch(e => { if (!cancelled) setError(e as Error); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [client, projectId]);

  const flush = useCallback(async (pending: Record<string, { lotCode: string; fieldName: string; value: unknown }>) => {
    if (!client || !projectId || !Object.keys(pending).length) return;
    const svc = new CctpService(client);
    const grouped: Record<string, Record<string, unknown>> = {};
    for (const { lotCode, fieldName, value } of Object.values(pending)) {
      grouped[lotCode] = grouped[lotCode] ?? {};
      grouped[lotCode][fieldName] = value;
    }
    try {
      for (const [lotCode, values] of Object.entries(grouped)) {
        if (lotCode === 'project') {
          await client.write('trace.cctp.project', [projectId], values);
        } else {
          await svc.saveLot(projectId, lotCode, values);
        }
      }
    } catch (e) {
      setError(e as Error);
    }
  }, [client, projectId]);

  const setField = useCallback((lotCode: string, fieldName: string, value: unknown) => {
    if (lotCode === 'project') {
      setProject(p => p ? { ...p, [fieldName]: value } : p);
    } else {
      setLots(ls => ({ ...ls, [lotCode]: { ...ls[lotCode], [fieldName]: value } }));
    }
    setDirty(d => {
      const next = { ...d, [`${lotCode}.${fieldName}`]: { lotCode, fieldName, value } };
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        setDirty(current => { flush(current); return {}; });
      }, debounceMs);
      return next;
    });
  }, [debounceMs, flush]);

  return { project, lots, loading, error, setField, isDirty: Object.keys(dirty).length > 0 };
}

export function useCctpProjects(client: OdooClient | null) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!client) return;
    setLoading(true);
    const svc = new CctpService(client);
    svc.listProjects()
      .then(p => setProjects(p as Project[]))
      .catch(e => setError(e as Error))
      .finally(() => setLoading(false));
  }, [client]);

  return { projects, loading, error };
}
